import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GATE_TEXT = {
  fr: { soon: "Bientôt", soonTitle: "Le rituel arrive.", soonSub: "Cacao, café et adaptogènes, conçus en Suisse. Laissez votre email pour être prévenu·e du lancement.", days: "jours", hours: "heures", min: "min", sec: "sec", notify: "Me prévenir", thanks: "Merci — à très vite.", maint: "Maintenance", maintTitle: "Une courte pause.", preview: "Accès préversion", pw: "Mot de passe", enter: "Entrer", bad: "Mot de passe incorrect" },
  en: { soon: "Coming soon", soonTitle: "The ritual is coming.", soonSub: "Cacao, coffee and adaptogens, crafted in Switzerland. Leave your email to be notified at launch.", days: "days", hours: "hours", min: "min", sec: "sec", notify: "Notify me", thanks: "Thank you — see you soon.", maint: "Maintenance", maintTitle: "A short pause.", preview: "Preview access", pw: "Password", enter: "Enter", bad: "Wrong password" },
};

export const Countdown = ({ date, l }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const iv = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(iv); }, []);
  const diff = Math.max(0, new Date(date) - now) / 1000;
  const parts = [[Math.floor(diff / 86400), l.days], [Math.floor(diff / 3600) % 24, l.hours], [Math.floor(diff / 60) % 60, l.min], [Math.floor(diff) % 60, l.sec]];
  return (
    <div className="flex gap-6 sm:gap-10 mt-10" data-testid="coming-soon-countdown">
      {parts.map(([n, lab]) => <div key={lab}><p className="font-serif text-4xl sm:text-6xl text-[#EFEAE1] tabular-nums">{String(n).padStart(2, "0")}</p><p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#EFEAE1]/40 mt-1">{lab}</p></div>)}
    </div>
  );
};

export const NotifyForm = ({ l }) => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const notify = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/newsletter`, { email }); setDone(true); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  if (done) return <p className="mt-10 text-sm text-[#4A624E]" data-testid="coming-soon-thanks">{l.thanks}</p>;
  return (
    <form onSubmit={notify} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md">
      <input data-testid="coming-soon-email" required type="email" placeholder="email@exemple.ch" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1 rounded-full bg-white/5 border border-white/15 px-5 py-3.5 text-sm placeholder:text-[#EFEAE1]/30 focus:outline-none focus:border-[#C85A32]" />
      <button data-testid="coming-soon-submit" className="rounded-full bg-[#EFEAE1] text-stone-900 px-7 py-3.5 text-[11px] uppercase tracking-[0.2em] hover:bg-[#C85A32] hover:text-white transition-colors">{l.notify}</button>
    </form>
  );
};

export const PreviewAccessForm = ({ l, isAdmin, onUnlock, onBackToAdmin }) => {
  const [pw, setPw] = useState("");
  const unlock = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/preview-access`, { password: pw }); onUnlock(); }
    catch (err) { toast.error(err.response?.status === 429 ? "Trop de tentatives — réessayez plus tard" : l.bad); }
  };
  return (
    <form onSubmit={unlock} className="flex items-center gap-2 self-end text-[11px]" data-testid="preview-access-form">
      {isAdmin && <button type="button" data-testid="admin-back-to-site" onClick={onBackToAdmin} className="mr-4 rounded-full bg-amber-400 text-stone-900 px-4 py-2 text-[10px] uppercase tracking-[0.15em]">Revenir à la vue admin</button>}
      <Lock size={12} className="text-[#EFEAE1]/40" /><span className="text-[#EFEAE1]/40 font-mono uppercase tracking-[0.2em] hidden sm:inline">{l.preview}</span>
      <input data-testid="preview-password" type="password" placeholder={l.pw} value={pw} onChange={(e) => setPw(e.target.value)} className="w-36 rounded-full bg-white/5 border border-white/15 px-4 py-2 text-xs placeholder:text-[#EFEAE1]/30 focus:outline-none" />
      <button data-testid="preview-submit" className="rounded-full border border-white/20 px-4 py-2 text-[10px] uppercase tracking-[0.15em] hover:bg-[#EFEAE1] hover:text-stone-900 transition-colors">{l.enter}</button>
    </form>
  );
};

export const GateWall = ({ mode, config, lang, setLang, isAdmin, onUnlock, onBackToAdmin }) => {
  const l = GATE_TEXT[lang];
  const soon = mode === "coming_soon";
  return (
    <div className="min-h-screen bg-[#1C1917] text-[#EFEAE1] flex flex-col px-6 lg:px-14 xl:px-24 py-10" data-testid={`site-gate-${mode}`}>
      <div className="flex items-center justify-between">
        <p className="font-serif text-2xl tracking-[0.28em]">NŪRA</p>
        <div className="flex gap-2 text-[11px] font-mono uppercase">{["fr", "en"].map((x) => <button key={x} data-testid={`gate-lang-${x}`} onClick={() => setLang(x)} className={`px-2 py-1 rounded-full ${lang === x ? "bg-[#EFEAE1] text-stone-900" : "text-[#EFEAE1]/50"}`}>{x}</button>)}</div>
      </div>
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="flex-1 flex flex-col justify-center max-w-3xl">
        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#C85A32]">{soon ? l.soon : l.maint}</p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl mt-4 leading-[1.02]">{soon ? l.soonTitle : l.maintTitle}</h1>
        <p className="mt-6 text-base text-[#EFEAE1]/70 max-w-xl leading-relaxed">{soon ? l.soonSub : (config.maintenance_message?.[lang] || config.maintenance_message?.fr)}</p>
        {soon && config.launch_date && <Countdown date={config.launch_date} l={l} />}
        {soon && <NotifyForm l={l} />}
      </motion.div>
      <PreviewAccessForm l={l} isAdmin={isAdmin} onUnlock={onUnlock} onBackToAdmin={onBackToAdmin} />
    </div>
  );
};
